/**
 * Shared types for analytics functionality across the application.
 * This file contains types used by analytics pages, components, and widgets.
 */

/**
 * Supported chart types for analytics widgets
 */
export enum ChartType {
    LINE = "line",
    BAR = "bar",
    PIE = "pie",
    AREA = "area",
    RADAR = "radar"
}

/**
 * Configuration for a single metric displayed in a widget
 */
export type MetricConfig = {
    key: string;
    name: string;
    color: string;
};

/**
 * Theme configuration passed to widgets and components
 */
export type WidgetTheme = {
    background: string;
    text: string;
    chartBg: string;
    border: string;
};

/**
 * Available filter options fetched from API
 */
export type AnalyticsFilterOptions = {
    attributionIds: string[];
    brandIds: string[];
    deviceTypes: string[];
};

/**
 * Filter values passed from parent to widgets
 * Arrays allow multi-select for attribution, brand, and device filters
 */
export type AnalyticsFilters = {
    startDate: string;
    endDate: string;
    attributionId: string[];  // Multi-select
    brandId: string[];        // Multi-select
    deviceType: string[];     // Multi-select
    focusField?: string;      // Field to group by for stacked bar charts (e.g., attribution_id, device_type, brand_id)
    focusSize?: number;       // Number of focus values to show (1-30, default 10)
};

/**
 * Timezone options for date display
 */
export type TimezoneOption = "local" | "UTC" | string;

/**
 * Predefined timezone choices for the dropdown
 */
export const TIMEZONE_OPTIONS: { value: TimezoneOption; label: string }[] = [
    { value: "local", label: "Browser Timezone" },
    { value: "UTC", label: "UTC" },
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Anchorage", label: "Alaska Time (AKT)" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
    { value: "Europe/London", label: "London (GMT/BST)" },
    { value: "Europe/Paris", label: "Paris (CET/CEST)" },
    { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
    { value: "Asia/Tokyo", label: "Tokyo (JST)" },
    { value: "Asia/Shanghai", label: "Shanghai (CST)" },
    { value: "Asia/Singapore", label: "Singapore (SGT)" },
    { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
    { value: "Asia/Dubai", label: "Dubai (GST)" },
    { value: "Asia/Kolkata", label: "India (IST)" },
    { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)" }
];

/**
 * Supported trendline types for analytics charts
 */
export enum TrendlineType {
    CONNECTED = "connected",    // Simple line connecting data points
    LINEAR = "linear",          // Linear regression (y = mx + b)
    LOGARITHMIC = "logarithmic", // Logarithmic regression (y = a + b*ln(x))
    EXPONENTIAL = "exponential", // Exponential regression (y = a*e^(bx))
    POLYNOMIAL = "polynomial",   // Polynomial regression (degree 2)
    MOVING_AVERAGE = "moving_average", // Simple moving average
    EMA = "ema",                // Exponential moving average
    LOESS = "loess",            // LOESS/LOWESS local smoothing
    SPLINE = "spline"           // Cubic spline interpolation
}

/**
 * Chart display options configured via the options modal
 */
export type ChartOptions = {
    showTrendline: boolean;
    trendlineType: TrendlineType;
    focusSize: number;  // Number of focus values to show when grouping (1-30, default 10)
};

/**
 * Simple data point for non-grouped charts
 */
export type DataPoint = {
    key: string;
    count: number;
};

/**
 * Stacked data point for grouped charts (matches backend pivot output)
 */
export type StackedDataPoint = {
    key: string;
    [focusValue: string]: string | number;
};

/**
 * Response shape when focus_field is set (pivoted by backend)
 */
export type PivotedApiResponse = {
    result: StackedDataPoint[];
    /* eslint-disable camelcase */
    focus_values: string[];
    /* eslint-enable camelcase */
    pivoted: true;
};

/**
 * Response shape for regular (non-grouped) data
 */
export type SimpleApiResponse = {
    result: DataPoint[];
    pivoted?: false;
};

/**
 * Union type for widget API responses
 */
export type WidgetApiResponse = PivotedApiResponse | SimpleApiResponse;

/**
 * Date preset styles for filter components
 * - "presets": Standard presets (T, 3, W, M, LM, Y) - default
 * - "period": Period selector (7, 14, 30 days) - used by Business Impressions
 * - "none": No date presets shown
 */
export type DatePresetStyle = "presets" | "period" | "none";

/**
 * Standard date presets (Today, 3 days, Week, Month, Last Month, Year)
 */
export type DatePreset = "T" | "3" | "W" | "M" | "LM" | "Y";

/**
 * Period presets (7, 14, 30 days)
 */
export type PeriodPreset = 7 | 14 | 30;

/**
 * Configuration for which filters support the "Group By" functionality
 */
export type GroupByConfig = {
    deviceType?: boolean;
    brandId?: boolean;
    attributionId?: boolean;
};

/**
 * Configuration for analytics page filters
 * Standardizes how filter components behave across all analytics pages
 */
export type AnalyticsFilterConfig = {
    /** Whether filters are enabled at all */
    enabled: boolean;
    /** Style of date presets to show */
    datePresetStyle: DatePresetStyle;
    /** Default date preset to select on page load */
    defaultDatePreset?: DatePreset;
    /** Default period preset (for "period" style) */
    defaultPeriodPreset?: PeriodPreset;
    /** Show start date filter */
    showStartDate?: boolean;
    /** Show end date filter */
    showEndDate?: boolean;
    /** Show brand filter */
    showBrandFilter?: boolean;
    /** Show attribution filter */
    showAttributionFilter?: boolean;
    /** Show device type filter */
    showDeviceFilter?: boolean;
    /** Configuration for Group By functionality per filter */
    enableGroupBy?: GroupByConfig;
    /** Show apply button (vs auto-apply on change) */
    showApplyButton?: boolean;
};

/**
 * Default filter configuration - enabled with standard presets
 */
export const DEFAULT_FILTER_CONFIG: AnalyticsFilterConfig = {
    enabled: true,
    datePresetStyle: "presets",
    defaultDatePreset: "W",
    showStartDate: true,
    showEndDate: true,
    showBrandFilter: false,
    showAttributionFilter: false,
    showDeviceFilter: false,
    enableGroupBy: {},
    showApplyButton: true
};

/**
 * Disabled filter configuration - shows filter bar but disabled
 */
export const DISABLED_FILTER_CONFIG: AnalyticsFilterConfig = {
    enabled: false,
    datePresetStyle: "none",
    showStartDate: false,
    showEndDate: false,
    showBrandFilter: false,
    showAttributionFilter: false,
    showDeviceFilter: false,
    showApplyButton: false
};

/**
 * Period selector filter configuration - for Business Impressions style
 */
export const PERIOD_FILTER_CONFIG: AnalyticsFilterConfig = {
    enabled: true,
    datePresetStyle: "period",
    defaultPeriodPreset: 7,
    showStartDate: false,
    showEndDate: false,
    showBrandFilter: false,
    showAttributionFilter: false,
    showDeviceFilter: false,
    showApplyButton: false
};

/**
 * Schema entry describing a single field returned by a widget API response
 */
export type WidgetFieldSchema = {
    description: string;
};

/**
 * Widget-level metadata returned alongside API results
 */
export type WidgetApiMeta = {
    /* eslint-disable camelcase */
    display_name: string;
    /* eslint-enable camelcase */
    description: string;
};

/**
 * An entry in the documentation sidebar, populated from a widget API response
 */
export type DocsSidebarEntry = {
    widgetKey: string;
    displayName: string;
    description: string;
    schema?: Record<string, WidgetFieldSchema>;
};

/**
 * Export schedule types are defined in services/export/types.ts
 * Re-exported here for convenience in analytics contexts.
 */
export type {
    ExportFormat,
    ExportFrequency,
    ExportScheduleStatus,
    DayOfWeek,
    ExportSchedule,
    ExportScheduleRequest,
    ExportScheduleListResponse
} from "../services/export/types";
